import { useEffect, useState } from "react";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import { getChapterContent } from "../lib/api";
import { safeHtml } from "../lib/safeHtml";

// Bundled fallback copy — used if the API is unreachable OR a field is empty
// in the DB. This mirrors the original hardcoded page 1:1 so the visual layout
// is guaranteed to never break, even offline.
const FALLBACK = {
  title: "His Story, Our Yes",
  subtitle: "One whisper, one God Wink, one \u201cyes\u201d at a time.",
  intro:
    "How a simple T-shirt logo became The Salty Moo\u2014one God Wink, one whisper, and one \u201cyes\u201d at a time.",
  body_html: null, // null = render the original static JSX body (see below)
  featured_quote: "",
  button_text: "Meet the Lil Salties\u2122",
  hero_image_url: "/assets/heroes/his-story.webp",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const resolveHero = (url) => {
  if (!url) return FALLBACK.hero_image_url;
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

// The original static body — kept so we can fall back to pixel-perfect JSX
// when the DB has never been touched. Once the admin edits body_html, we
// render the sanitized HTML through the styled `.story-body-html` wrapper
// below (which mirrors the same typography).
const StaticBody = () => (
  <>
    <p>
      The Salty Moo began with a simple dream to create T-shirts. Looking
      back, we can now see that God was writing a much bigger story than
      we could have imagined.
    </p>
    <p>
      For years, Stefanie dreamed of writing a book based on her personal
      testimony from a lifetime ago about domestic violence. She attended
      Author School, faithfully completed the program, and graduated with
      every intention of publishing that book. Although the manuscript
      was written, the editing was never finished. What once felt
      disappointing now feels like God&rsquo;s gentle redirection. He
      wasn&rsquo;t saying, &ldquo;No.&rdquo; He was simply saying,
      &ldquo;Not this story.&rdquo;
    </p>
    <p>
      During Author School, Stefanie also discovered something that
      changed the way she recognized God&rsquo;s leading. She realized
      that He often speaks to her heart most clearly through the quiet
      beauty of His creation. Looking back now, it&rsquo;s no surprise
      that some of His sweetest whispers came while walking among long
      ears, shaggy Highland cattle, and peaceful pastures.
    </p>
    <p className="font-display italic text-xl md:text-2xl text-[#4B3A2F] text-center py-2">
      Sometimes God plants a dream in our hearts&mdash;not because
      it&rsquo;s the final destination, but because it&rsquo;s preparing
      us for the story He intends to tell.
    </p>
    <p>Then life took a beautiful turn.</p>
    <p>
      We purchased a little piece of land. Soon came the Saltie Brays,
      followed by the Saltie Moos. Somewhere between muddy boots, long
      ears, shaggy coats, and peaceful mornings in the pasture, God began
      whispering His next ideas.
    </p>
    <p>
      We never set out to build a storybook world&mdash;we simply kept
      saying &ldquo;yes&rdquo; to the next whisper.
    </p>
    <p>One by one, He gently revealed them.</p>
    <ul className="list-none space-y-2 font-display italic text-lg md:text-xl text-[#4B3A2F] text-center">
      <li>Matthew 5:13 became Salt of the Herd.</li>
      <li>Salt of the Herd became Saltie Moos and Saltie Brays.</li>
      <li>
        Then came Lil Salties<sup>&trade;</sup>.
      </li>
    </ul>
    <p>
      Along the way, there were little God Winks&mdash;those quiet
      moments when He lovingly confirmed we were walking the path He had
      prepared. Then one day, we smiled as we realized those same God
      Winks had become Saltie Winks<sup>&trade;</sup> in the very
      storybook world He&rsquo;d been creating all along.
    </p>
    <p>
      Today, as our publishing team and illustrator bring the first books
      to life, we&rsquo;re simply grateful to watch His plans unfold. He
      never revealed the whole story. He simply gave us the next idea...
      and then the next.
    </p>
    <p>
      Looking back, it&rsquo;s clear this story was never really ours to
      write.
    </p>
    <p>
      It has always been His story. We simply had the privilege of saying
      &ldquo;yes&rdquo; one whisper, one God Wink, and one idea at a
      time.
    </p>
    <p className="font-display italic text-xl md:text-2xl text-[#4B3A2F] text-center py-4">
      And today, every Saltie Wink<sup>&trade;</sup> is our gentle
      reminder that God is still writing the story&hellip; one little
      idea at a time.
    </p>
  </>
);

export default function HisStory() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    getChapterContent("his-story")
      .then((data) => setContent(data))
      .catch(() => setContent({})); // network error → use full fallback
  }, []);

  if (!content) {
    // Brief loading state (matches other chapter loading treatment)
    return (
      <div
        className="parchment min-h-screen flex items-center justify-center"
        data-testid="his-story-page"
      >
        <SEO
          title="His Story, Our Yes"
          description="How a simple T-shirt logo became The Salty Moo — one God Wink, one whisper, and one 'yes' at a time."
          path="/his-story"
        />
        <p className="font-script text-2xl text-[#4B3A2F]">a quiet moment...</p>
      </div>
    );
  }

  const title = content.title || FALLBACK.title;
  const subtitle = content.subtitle || FALLBACK.subtitle;
  const intro = content.intro || FALLBACK.intro;
  const featuredQuote = content.featured_quote || "";
  const buttonText = content.button_text || FALLBACK.button_text;
  const heroImage = resolveHero(content.hero_image_url);
  const bodyHtml = content.body_html || "";
  const featurePhotoUrl = content.feature_photo_url || "";
  const featurePhotoCaption =
    content.feature_photo_caption ||
    "Stefanie in the pasture with the herd.";
  const featurePhotoResolved = featurePhotoUrl
    ? (featurePhotoUrl.startsWith("http") ? featurePhotoUrl : `${BACKEND}${featurePhotoUrl}`)
    : "";

  return (
    <div className="parchment min-h-screen" data-testid="his-story-page">
      <SEO
        title={title}
        description="How a simple T-shirt logo became The Salty Moo — one God Wink, one whisper, and one 'yes' at a time."
        path="/his-story"
      />

      <ChapterHero
        path="/his-story"
        image={heroImage}
        title={title}
        subtitle={subtitle}
        position="top-left"
        contentStyle={{
          width: "fit-content",
          maxWidth: "none",
          marginLeft: "auto",
          marginRight: "auto",
          whiteSpace: "nowrap",
        }}
        subtitleStyle={{ paddingLeft: "clamp(5rem, 11.25vw, 13.5rem)" }}
        subtitleColor="#120A05"
      />

      <article className="max-w-4xl mx-auto -mt-8 relative z-10 page-shadow parchment-deep border-2 border-[#E7DFD5] px-8 md:px-16 py-16 md:py-20 mb-24">
        {/* Header */}
        <header className="text-center mb-14">
          <p
            className="mt-2 font-script text-2xl md:text-3xl text-[#4B3A2F] max-w-2xl mx-auto leading-snug"
            data-testid="his-story-intro"
          >
            {intro}
          </p>
          <div className="gold-rule mt-8" />
        </header>

        {/* Optional featured pull-quote — only rendered when the admin sets one */}
        {featuredQuote && (
          <blockquote
            data-testid="his-story-featured-quote"
            className="max-w-2xl mx-auto font-display italic text-xl md:text-2xl text-[#4B3A2F] text-center py-2 mb-10"
          >
            {featuredQuote}
          </blockquote>
        )}

        {/* Story body */}
        <section
          data-testid="his-story-body"
          className="story-body-html max-w-2xl mx-auto font-body text-[#2C2724] text-lg md:text-xl leading-[1.85] space-y-6"
        >
          {bodyHtml ? (
            <div dangerouslySetInnerHTML={safeHtml(bodyHtml)} />
          ) : (
            <StaticBody />
          )}
        </section>

        {/* Polaroid photo of Stefanie in the pasture. When feature_photo_url
             is empty we render the "coming soon" placeholder; once uploaded
             the real image slots into the same frame (identical dimensions,
             no layout shift). */}
        <div className="mt-16 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-[#4B3A2F] mb-3">
            A photograph
            {!featurePhotoUrl && (
              <>
                ,{" "}
                <span className="coming-soon-flash">coming soon</span>
              </>
            )}
          </p>
          <div
            className="relative mx-auto w-full sm:w-[70%] md:w-[64%]"
            style={{ transform: "rotate(-0.4deg)" }}
          >
            <div
              className="relative p-5 sm:p-7"
              style={{
                background:
                  "radial-gradient(ellipse at 30% 20%, #FBF3E1 0%, #F5EDDE 55%, #EFE4CE 100%)",
                borderRadius: "22px",
                boxShadow:
                  "0 2px 0 rgba(255,255,255,0.7) inset, 0 -1px 0 rgba(0,0,0,0.05) inset, 0 30px 40px -22px rgba(75,58,47,0.45), 0 0 0 1px rgba(168,113,73,0.18)",
              }}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-2 rounded-[18px]"
                style={{
                  boxShadow:
                    "inset 0 0 40px rgba(126,146,104,0.10), inset 0 0 0 2px rgba(126,146,104,0.18)",
                }}
              />
              {featurePhotoUrl ? (
                <div
                  data-testid="his-story-feature-photo"
                  className="relative aspect-[4/3] w-full overflow-hidden"
                  style={{
                    borderRadius: "14px",
                    boxShadow:
                      "inset 0 0 0 1px rgba(126,146,104,0.25)",
                  }}
                >
                  <img
                    src={featurePhotoResolved}
                    alt={featurePhotoCaption}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  data-testid="his-story-photo-placeholder"
                  className="relative aspect-[4/3] w-full flex flex-col items-center justify-center gap-4 text-center px-6 overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(160deg, #F9F1E1 0%, #EFE4CE 60%, #E3D3B4 100%)",
                    borderRadius: "14px",
                    boxShadow:
                      "inset 0 0 60px rgba(75,58,47,0.15), inset 0 0 0 1px rgba(126,146,104,0.25)",
                  }}
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 100%, rgba(126,146,104,0.30), transparent 70%)",
                    }}
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-24"
                    style={{
                      background:
                        "radial-gradient(ellipse at 50% 0%, rgba(238,232,79,0.35), transparent 70%)",
                    }}
                  />
                  <div className="text-6xl md:text-7xl select-none" aria-hidden>
                    📷
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#6D5A50]">
                    Future Photo
                  </p>
                  <p className="font-script text-2xl md:text-3xl text-[#4B3A2F] max-w-md">
                    {featurePhotoCaption}
                  </p>
                </div>
              )}
            </div>
            {featurePhotoUrl && (
              <p
                data-testid="his-story-feature-photo-caption"
                className="mt-4 font-script text-xl md:text-2xl text-[#4B3A2F]"
              >
                {featurePhotoCaption}
              </p>
            )}
          </div>
        </div>
      </article>

      <NextPage to="/lil-salties" label={buttonText} />
    </div>
  );
}
